import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isDate } from 'validator';

// Our Components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import SoraTextField from 'components/Input/SoraTextField';
import TextInput from 'components/Input/TextInput';
import USStateDropdown from 'components/Dropdown/USStateDropdown';
import YearDatePicker from 'components/DatePicker/YearDatePicker';
import { NUMBER, TEXT } from 'components/Input/Types';

// Our hooks 🪝
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Create Deal Hook
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';

// Utils
import {
	clearFormValues,
	dollarFormatter,
	getDateFromDateObject,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Constants
import { AUTO, NEW_LOAN_OFFER_TYPE } from 'shared/constants';

// Our PropTypes
import {
	advisorInfoProps,
	clientInfoProps,
	financialDataProps
} from 'shared/propTypes/lendingPropTypes';

function NewAutoForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	financialData,
	loanRequestId,
	loanType,
	postSuccessEvent
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const updateLoanInquiry = useMutateUpdateLoanInquiry();
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const { clientFullName, creditScoreValue, clientId, firstName, lastName } =
		clientInfo;

	const [vehicleState, setVehicleState] = useState('');
	const [vehicleCounty, setVehicleCounty] = useState('');
	const [estimatedPurchaseValue, setEstimatedPurchaseValue] = useState('');
	const [downPaymentPercent, setDownPaymentPercent] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [loanTenure, setLoanTenure] = useState('');
	const [vehicleCondition, setVehicleCondition] = useState('New');
	const [yearOfVehicle, setYearOfVehicle] = useState(new Date());
	const [dealershipSale, setDealershipSale] = useState();
	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const formData = [isClientNameProvided, estimatedPurchaseValue, loanAmount];

	const loanRequestDetailsRequiredFields = [
		estimatedPurchaseValue,
		downPaymentPercent,
		loanAmount,
		loanTenure,
		yearOfVehicle
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl ? loanRequestDetailsRequiredFields : formData
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `New Automotive Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Condition of vehicle intended to purchase: ${vehicleCondition}`,
				`Year of vehicle: ${getDateFromDateObject(yearOfVehicle)}`,
				`Is this a dealership vehicle sale?: ${dealershipSale}`,
				`Client's county: ${vehicleCounty}`,
				`Client's state: ${vehicleState}`,
				`Client's estimated purchase value: ${dollarFormatter.format(
					+estimatedPurchaseValue
				)}`,
				`Client's down payment: ${downPaymentPercent}%`,
				`Client's ideal loan tenure: ${loanTenure}`
			],
			additionalNotes,
			lendingTypeRequested: 'New Automotive Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setVehicleCondition,
						setVehicleCounty,
						setDealershipSale,
						setDownPaymentPercent,
						setEstimatedPurchaseValue,
						setLoanAmount,
						setLoanTenure,
						setVehicleState,
						setYearOfVehicle
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			clientId,
			condition: vehicleCondition,
			county: vehicleCounty,
			creditScoreValue,
			dealershipSale,
			downPaymentPercent,
			estimatedPurchaseValue,
			firstName,
			lastName,
			loanAmount,
			loanTenure,
			offerType: NEW_LOAN_OFFER_TYPE,
			state: vehicleState,
			tradelineType: AUTO,
			yearOfVehicle
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			clientId,
			condition: vehicleCondition,
			county: vehicleCounty,
			creditScoreValue,
			dealershipSale,
			downPaymentPercent,
			estimatedPurchaseValue,
			firstName,
			lastName,
			loanAmount,
			loanTenure,
			offerType: NEW_LOAN_OFFER_TYPE,
			state: vehicleState,
			tradelineType: AUTO,
			yearOfVehicle
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setVehicleCondition,
						setVehicleCounty,
						setDealershipSale,
						setDownPaymentPercent,
						setEstimatedPurchaseValue,
						setLoanAmount,
						setLoanTenure,
						setVehicleState,
						setYearOfVehicle
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'percent',
				value: downPaymentPercent,
				label: 'Down Payment Percent:'
			},
			{
				type: 'currency',
				value: estimatedPurchaseValue,
				label: 'Estimated Purchase Value:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Loan Amount:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				County: ${vehicleCounty === '' ? 'N/A' : vehicleCounty}
				Condition: ${vehicleCondition}
				Dealership Sale: ${dealershipSale === '' ? 'N/A' : dealershipSale}
				${formattedCollection}
				Loan Tenure: ${loanTenure === '' ? 'N/A' : loanTenure}
				Year of Vehicle: ${
					// @ts-ignore
					yearOfVehicle === '' ? 'N/A' : yearOfVehicle
				}
				State: ${vehicleState}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	const isValidDate = useMemo(() => {
		const isStringDate = typeof yearOfVehicle === 'string';
		const isDateType = yearOfVehicle instanceof Date;
		if (isStringDate && yearOfVehicle !== '') {
			const isDateCheckWithConversion = isDate(new Date(yearOfVehicle));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(yearOfVehicle);
		}
		// if yearOfVehicle is null this returns false;
		return false;
	}, [yearOfVehicle]);

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						New Automotive Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{savingLoanInquiry && <Loader />}
			{!false && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* State */}
						<USStateDropdown
							label="State of purchase *"
							state={vehicleState}
							setState={setVehicleState}
							inputProps={{
								'data-test': 'state'
							}}
							gridColumns={12}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* County */}
						<TextInput
							label="County"
							value={vehicleCounty}
							onChange={setVehicleCounty}
							type={TEXT}
							inputProps={{
								'data-test': 'county'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Estimated Purchase Value */}
						<CurrencyTextInput
							label="Vehicle purchase value *"
							value={estimatedPurchaseValue}
							onChange={setEstimatedPurchaseValue}
							inputProps={{
								'data-test': 'estimatedPurchaseValue'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Down payment % */}
						<TextInput
							type="percent"
							label={
								atLoanRequestDetailsUrl
									? 'Down payment % *'
									: 'Down payment %'
							}
							helperText="Down payment cannot be empty"
							onChange={setDownPaymentPercent}
							value={downPaymentPercent}
							inputProps={{
								'data-test': 'downPaymentPercent'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Desired Loan Amount */}
						<CurrencyTextInput
							label="Desired loan amount *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Tenure */}
						<TextInput
							id="loanTenure"
							label={
								atLoanRequestDetailsUrl
									? 'Desired loan term *'
									: 'Desired loan term'
							}
							subLabel="Enter time in years"
							type={TEXT}
							name="Ideal loan length"
							onChange={setLoanTenure}
							value={loanTenure}
							inputProps={{
								'data-test': 'loanTenure'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Dropdown
							items={['New', 'Used']}
							selected={vehicleCondition}
							onChange={setVehicleCondition}
							variant="outlined"
							label="Vehicle condition"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<YearDatePicker
							id="yearOfVehicle"
							label={
								atLoanRequestDetailsUrl
									? 'Vehicle year *'
									: 'Vehicle year'
							}
							onChange={setYearOfVehicle}
							value={yearOfVehicle}
							error={yearOfVehicle === null || !isValidDate}
							errorText="Year of vehicle needs to be a valid date"
							inputProps={{
								'data-test': 'yearOfVehicle'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Dropdown
							items={['Auto Dealership', 'Private Sale', 'Other']}
							selected={dealershipSale}
							onChange={setDealershipSale}
							variant="outlined"
							label="Point of purchase"
						/>
					</Grid>
					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

NewAutoForm.propTypes = {
	advisorInfo: advisorInfoProps.isRequired,
	clientInfo: clientInfoProps.isRequired,
	financialData: financialDataProps.isRequired
};

export default NewAutoForm;
